import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse } from '../types';
import { clearStoredActiveTenantId, getStoredActiveTenantId } from './tenantStorage';

// ============================================
// API Client Configuration
// ============================================

const viteEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env;

const API_BASE_URL = resolveApiBaseUrl();
const CSRF_BOOTSTRAP_PATH = '/auth/web/csrf';
const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';

export type ApiRequestConfig = AxiosRequestConfig & {
  handleAccountSetup?: boolean;
  assertRequestCurrent?: () => void;
  _retry?: boolean;
  _csrfRetry?: boolean;
};

interface CsrfBootstrapResponse {
  token: string;
  headerName?: string;
  parameterName?: string;
}

let csrfTokenCache: string | null = null;
let csrfBootstrapPromise: Promise<string | null> | null = null;

function resolveApiBaseUrl(): string {
  const configured = viteEnv?.VITE_API_URL || viteEnv?.VITE_API_BASE_URL || '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'preview.playtark.com' && isLocalApiBaseUrl(configured)) {
    return 'https://api-preview.playtark.com';
  }

  return configured || 'http://localhost:8080';
}

function isLocalApiBaseUrl(value: string): boolean {
  if (!value) {
    return true;
  }

  try {
    const { hostname } = new URL(value);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  } catch {
    return false;
  }
}

/**
 * Create axios instance with BFF cookie support
 * - withCredentials: true enables cookies for cross-origin requests
 * - This is critical for the BFF session cookie pattern
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // CRITICAL: Enables sending/receiving cookies
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Helps identify AJAX requests
  },
});

// ============================================
// Request Interceptor
// ============================================

apiClient.interceptors.request.use(
  async (config) => {
    const assertCurrent = (config as ApiRequestConfig).assertRequestCurrent;
    assertCurrent?.();
    attachTenantHeader(config);
    clearJsonContentTypeForFormData(config);

    if (requiresConsumerWebCsrf(config)) {
      const csrfToken = await ensureCsrfToken();
      assertCurrent?.();
      if (csrfToken) {
        config.headers = withHeader(config.headers, CSRF_HEADER_NAME, csrfToken);
      }
    }

    assertCurrent?.();
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// Response Interceptor
// ============================================

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as ApiRequestConfig;

    const setupNextStep = getAccountSetupNextStep(error);
    const recoveryData = error.response?.data as { error?: string; errorCode?: string } | undefined;
    const recoveryCode = recoveryData?.errorCode || recoveryData?.error;
    const callerOwnsSetupFailure = originalRequest?.handleAccountSetup
      && (Boolean(setupNextStep) || recoveryCode === 'UNVERIFIED_EMAIL' || recoveryCode === 'UNVERIFIED_PHONE');
    if (setupNextStep && !originalRequest?.handleAccountSetup) {
      redirectToAccountSetup();
      return Promise.reject(error);
    }

    if (
      error.response?.status === 403 &&
      originalRequest &&
      !callerOwnsSetupFailure &&
      recoveryCode !== 'WEB_SIGN_IN_REQUIRES_MOBILE_ACCOUNT' &&
      !originalRequest._csrfRetry &&
      requiresConsumerWebCsrf(originalRequest)
    ) {
      originalRequest._csrfRetry = true;
      clearCsrfToken();
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        originalRequest.headers = withHeader(originalRequest.headers, CSRF_HEADER_NAME, csrfToken);
        return apiClient(originalRequest);
      }
    }

    // Handle unauthenticated sessions. The backend should return 401, but older
    // deployments returned a generic 403 for unauthenticated web sessions.
    if (isSessionAuthFailure(error, originalRequest) && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isNonRedirectAuthRequest(originalRequest.url)) {
        redirectToLogin('session_expired');
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// ============================================
// Helper Functions
// ============================================

function clearCsrfToken() {
  csrfTokenCache = null;
}

async function ensureCsrfToken(): Promise<string | null> {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = axios
      .get<CsrfBootstrapResponse>(CSRF_BOOTSTRAP_PATH, {
        baseURL: API_BASE_URL,
        withCredentials: true,
        timeout: 30000,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .then((response) => {
        csrfTokenCache = response.data.token ?? null;
        return csrfTokenCache;
      })
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }

  return csrfBootstrapPromise;
}

function isStateChangingMethod(method?: string): boolean {
  const stateMethods = ['post', 'put', 'patch', 'delete'];
  return stateMethods.includes(method?.toLowerCase() || '');
}

function requiresConsumerWebCsrf(config: AxiosRequestConfig): boolean {
  if (!isStateChangingMethod(config.method)) {
    return false;
  }

  const url = config.url || '';
  if (url.includes(CSRF_BOOTSTRAP_PATH)) {
    return false;
  }

  return url.startsWith('/auth/web/')
    || url.startsWith('/api/v1/web/')
    || url.startsWith('/api/v1/support/');
}

function isNonRedirectAuthRequest(url?: string): boolean {
  const requestUrl = url || '';
  return (
    requestUrl.includes('/auth/web/csrf') ||
    requestUrl.includes('/auth/web/start-login') ||
    requestUrl.includes('/auth/web/verify-otp') ||
    requestUrl.includes('/auth/web/start-email-login') ||
    requestUrl.includes('/auth/web/verify-email-otp') ||
    requestUrl.includes('/auth/web/me') ||
    requestUrl.includes('/auth/web/logout') ||
    requestUrl.includes('/auth/web/refresh') ||
    requestUrl.includes('/api/v1/web/session')
  );
}

function isSessionAuthFailure(error: AxiosError<ApiError>, request?: ApiRequestConfig): boolean {
  const status = error.response?.status;
  if (!status || !request) {
    return false;
  }

  if (status === 401) {
    return true;
  }

  if (status !== 403 || !isWebApiRequest(request.url)) {
    return false;
  }

  const data = error.response?.data as { error?: string; message?: string; code?: string } | undefined;
  const errorText = [data?.error, data?.message, data?.code].filter(Boolean).join(' ').toLowerCase();
  return errorText.includes('not authenticated') || errorText.includes('session expired');
}

function getAccountSetupNextStep(error: AxiosError<ApiError>): string | null {
  if (error.response?.status !== 403) {
    return null;
  }
  const data = error.response.data as { error?: string; nextStep?: string } | undefined;
  return data?.error === 'ACCOUNT_SETUP_REQUIRED' && data.nextStep ? data.nextStep : null;
}

function redirectToAccountSetup(): void {
  if (window.location.pathname === '/register') {
    return;
  }
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams();
  if (currentPath && currentPath !== '/') {
    params.set('return', currentPath);
  }
  window.location.href = `/register${params.size ? `?${params.toString()}` : ''}`;
}

function isWebApiRequest(url?: string): boolean {
  const requestUrl = url || '';
  return requestUrl.startsWith('/api/v1/web/') || requestUrl.includes('/api/v1/web/');
}

function redirectToLogin(reason: string): void {
  clearStoredActiveTenantId();
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ reason });
  if (currentPath && !currentPath.startsWith('/login')) {
    params.set('return', currentPath);
  }
  window.location.href = `/login?${params.toString()}`;
}

function attachTenantHeader(config: AxiosRequestConfig): void {
  const url = config.url || '';
  if (!url.startsWith('/api/v1/web/')) {
    return;
  }

  const tenantId = getStoredActiveTenantId();
  if (!tenantId) {
    return;
  }

  const nextHeaders =
    config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders((config.headers ?? {}) as Record<string, string>);
  nextHeaders.set('X-TARK-Tenant-Id', String(tenantId));
  config.headers = nextHeaders;
}

function clearJsonContentTypeForFormData(config: AxiosRequestConfig): void {
  if (!isFormDataPayload(config.data)) {
    return;
  }

  const nextHeaders =
    config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders((config.headers ?? {}) as Record<string, string>);
  nextHeaders.delete('Content-Type');
  config.headers = nextHeaders;
}

function isFormDataPayload(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function withHeader(
  headers: AxiosRequestConfig['headers'],
  name: string,
  value: string
): AxiosHeaders {
  const nextHeaders =
    headers instanceof AxiosHeaders
      ? headers
      : new AxiosHeaders((headers ?? {}) as Record<string, string>);
  nextHeaders.set(name, value);
  return nextHeaders;
}

// ============================================
// Typed API Methods
// ============================================

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data.data;
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data.data;
}

// ============================================
// Error Handling Utilities
// ============================================

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.code !== undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
